import Alert from 'components/alertComponent';
import Loader from 'components/loaderComponent';
import { useGetPage } from 'hooks/useGetPage';
import {
  pageIds,
  PROVIDER_SERVICE_PRICE_TAX,
  SUPPORT_EMAIL,
} from 'utilities/constants';
import { State } from 'country-state-city';
import ButtonComponent from 'components/button/buttonComponent';
import {
  AcuityWorkingHoursPerDay,
  AppointmentTypeEnum,
  Persona_Inquiry_Status_Enum,
  ProviderEducationOutput,
  ProviderStateLicenseOutput,
  ServiceOfferingsOutput,
  useConnectStripeAccountMutation,
  useGetAcuityProviderTimeSlotsLazyQuery,
  useGetUnauthenticatedQuestionsByIdsQuery,
} from 'graphql/generated/hasura';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'auth/context/AuthContext';
import useFhirProviderDataLoader from 'hooks/useFhirProviderDataLoader';

import {
  capitalizeFirstLetter,
  findQuestionById,
  formatCurrentDateToYYYYMMDD,
} from 'utilities/functions';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CodexFeatureFlags } from 'utilities/interfaces';
import {
  useGetUsersVerificationStatusesByEmail,
  UserVerificationStatus,
} from 'hooks/useUserVerificationStatus';
import { Roles } from '../../../../firebase/interfaces';
import {
  ProfileInfoField,
  ProfileInfoSection,
} from './components/ProviderInfoSection/ProviderInfoSection';
import ProviderAvatar from 'components/providerAvatarComponent';
import { OTHER } from 'components/dynamicQuestionnaire/utils/constants';
import moment from 'moment';
import { questionsIds } from '../my-profile/components/providerComponents/constants';
import { Question } from '../my-profile/components/providerComponents/personalInfo/interfaces';
import { DropdownItem } from 'components/dynamicQuestionnaire/interfaces/dynamicQuestionnaireResponse.interface';
import { useLocation, useNavigate } from 'react-router-dom';
import { DASHBOARD, MY_ACCOUNT_PROFILE } from 'utilities/routes';
import { ProviderProfileSectionTypes } from '../my-profile/enums';
import RequiredActions, { ACTIONS_COUNT } from './components/RequiredActions';
import { appointmentRenderInfo } from '../../../my-appointments/interfaces';
import { parseDuration } from '../../../my-appointments/helpers/groupServiceType';

const RESIDENCY_FORMAT_DATE = 'MM/YYYY';
const LICENSES_FORMAT_DATE = 'YYYY-MM-DD';

export const ProviderPendingApproval: React.FC = () => {
  const [schedule, setSchedule] = useState<AcuityWorkingHoursPerDay>();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);

  const code = queryParams.get('code');
  const isFirstSendConnectStripe = useRef(true);

  const [connectStripeAccountMutation] = useConnectStripeAccountMutation({
    refetchQueries: ['GetFHIRProviderbyEmail'],
  });

  const { data: questionsData, loading: questionsLoading } =
    useGetUnauthenticatedQuestionsByIdsQuery({
      variables: {
        ids: [questionsIds.LANGUAGES_QUESTION],
      },
    });

  const languagesQuestionData = (!questionsLoading &&
    questionsData &&
    findQuestionById(
      questionsData,
      questionsIds.LANGUAGES_QUESTION,
    )) as Question;

  const { user: loggedUser, profilePicture, setProviderEnabled } = useAuth();
  const { enablePersonaVerification } = useFlags<CodexFeatureFlags>();
  const {
    count,
    data,
    loading: loadingUserVerificationStatus,
  } = useGetUsersVerificationStatusesByEmail();

  const [userVerificationStatus, setUserVerificationStatus] =
    useState<UserVerificationStatus>({
      status: null,
    });

  const [getAcuityProviderTimeSlots] = useGetAcuityProviderTimeSlotsLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted(data) {
      if (data?.GetProviderTimeSlots) {
        setSchedule(data?.GetProviderTimeSlots.providerWorkingHours.data);
      }
    },
  });

  const states = State.getStatesOfCountry('US');

  const { fhirProviderDataLoading, providerProfileData } =
    useFhirProviderDataLoader(loggedUser, loggedUser?.email);

  const { data: localePendingReview, loading: loadingPendingReview } =
    useGetPage({
      locale: 'en',
      pageId: pageIds.PROVIDER_USER_PENDING_REVIEW,
    });

  const { data: localeMyProfile, loading: loadingMyProfile } = useGetPage({
    locale: 'en',
    pageId: pageIds.MY_PROFILE,
  });

  const getLabelByValue = (value: string, options: DropdownItem[] = []) => {
    return options.find((option) => option.value === value)?.label || value;
  };

  const validateUserStatus = useCallback(async () => {
    setUserVerificationStatus({ status: data?.verifyId });
    if (count === ACTIONS_COUNT) {
      setProviderEnabled(true);
    }
  }, [count, data, setProviderEnabled]);

  useEffect(() => {
    if (enablePersonaVerification && loggedUser?.role === Roles.PROVIDER) {
      validateUserStatus().catch((e) =>
        console.log(
          'An error happened while validating the user verification status',
          e,
        ),
      );
    }
  }, [enablePersonaVerification, loggedUser?.role, validateUserStatus]);

  const formatTimeRange = (range: string): string => {
    const [from, to] = range.split('-');
    const formatTime = (time: string) => time.toUpperCase();

    return `From ${formatTime(from)} to ${formatTime(to)}`;
  };

  const getSchedulePart = (
    schedule: AcuityWorkingHoursPerDay = {} as AcuityWorkingHoursPerDay,
  ): ProfileInfoField[] => {
    const days = [
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
      'Sunday',
    ];

    return days
      .map((day) => {
        const ranges = (schedule[day as keyof AcuityWorkingHoursPerDay] ||
          []) as string[];

        if (ranges.length === 0) return null;

        return {
          label: day,
          renderCustom: (
            <div className="flex flex-col">
              {ranges.map((range, index) => (
                <span className="text-med-gray-3 font-medium" key={index}>
                  {formatTimeRange(range)}
                </span>
              ))}
            </div>
          ),
        };
      })
      .filter(Boolean) as ProfileInfoField[];
  };

  const getBillingPart = (
    serviceOfferings: ServiceOfferingsOutput[] = [] as ServiceOfferingsOutput[],
  ): ProfileInfoField[] => {
    const billingByType = serviceOfferings.reduce((acc, item) => {
      const key =
        appointmentRenderInfo[
          item.serviceType.toUpperCase() as unknown as AppointmentTypeEnum
        ]?.label;

      const value = acc[key] || [];

      if (!item.active) return acc;

      const price = Number(item.price);

      const resultPrice = price / PROVIDER_SERVICE_PRICE_TAX;

      acc[key] = [
        ...value,
        {
          price: resultPrice.toFixed(0),
          duration: parseDuration(item.serviceType)?.total as unknown as string,
        },
      ];

      return acc;
    }, {} as Record<string, Record<string, string>[]>);

    return Object.entries(billingByType).map(([label, services]) => {
      return {
        label,
        renderCustom: (
          <div className="grid grid-cols-3 text-med-gray-3">
            {services.map((service, index) => {
              return (
                <div className="flex items-center gap-2.5">
                  {!!index && (
                    <div className="h-[5px] w-[5px] bg-graph-border-color rounded-xl" />
                  )}
                  $ {service?.price} ({service?.duration} min)
                </div>
              );
            })}
          </div>
        ),
      };
    });
  };

  const getLicensesPart = (
    stateLicenses: ProviderStateLicenseOutput[] = [],
  ) => {
    return stateLicenses.reduce((acc, item, index) => {
      const expDate = moment(item?.expirationDate).format(LICENSES_FORMAT_DATE);
      const state = states.find(({ isoCode }) => isoCode === item?.state);
      return [
        ...acc,
        { subtitle: `State License ${index + 1}` },
        {
          label: 'State',
          value: `${state?.name}, ${state?.isoCode}`,
        },
        {
          label: 'Expiration Date',
          value: expDate,
        },
        {
          label: 'License Number',
          value: item?.licenseNumber,
        },
      ];
    }, [] as ProfileInfoField[]);
  };

  const getEducationPart = (educations: ProviderEducationOutput[] = []) => {
    return educations.reduce((acc, item, index) => {
      const residencyStart = moment(item?.residencyStartDate).format(
        RESIDENCY_FORMAT_DATE,
      );
      const residencyEnd = !item.isStillResident
        ? moment(item?.residencyEndDate).format(RESIDENCY_FORMAT_DATE)
        : 'Current date';

      return [
        ...acc,
        { separator: true },
        { subtitle: `Education ${index + 1}` },
        {
          label: localePendingReview?.undergraduate,
          value: item?.undergraduate,
        },
        {
          label: localePendingReview?.medicalSchool,
          value: item?.medicalSchool,
        },
        {
          label: localePendingReview?.residency,
          value: item.residency,
        },
        {
          label: localePendingReview?.residencyPeriod,
          value: `${residencyStart} - ${residencyEnd}`,
        },
      ];
    }, [] as ProfileInfoField[]);
  };

  const accountInfoItems = [
    {
      renderCustom: <ProviderAvatar size={'xl'} imageUrl={profilePicture} />,
    },
    {
      label: localePendingReview?.firstName,
      value: providerProfileData?.firstname,
    },
    {
      label: localePendingReview?.lastName,
      value: providerProfileData?.lastname,
    },
  ];

  const personalInfoItems = [
    {
      label: localePendingReview?.dateOfBirth,
      value: moment(providerProfileData?.dateOfBirth as string).format(
        'DD/MM/YYYY',
      ),
    },
    {
      label: localePendingReview?.gender,
      value: capitalizeFirstLetter(providerProfileData?.gender as string),
    },
    {
      label: localePendingReview?.describeGender,
      value: providerProfileData?.genderDescribe,
      hidden: providerProfileData?.gender !== OTHER,
    },

    {
      label: localePendingReview?.languages,
      value: providerProfileData?.languagesOfCommunication?.map((item) =>
        !item || typeof item === 'string'
          ? getLabelByValue(
              item,
              languagesQuestionData.answers as unknown as DropdownItem[],
            )
          : item.label,
      ),
    },
    {
      label: localePendingReview?.aboutYou,
      value: providerProfileData?.about,
    },
  ];

  const stateLicensesItems = [
    {
      label: localePendingReview?.NPI,
      value: providerProfileData?.nationalProviderIdentifier,
    },
    ...getLicensesPart(
      providerProfileData?.stateLicenses as ProviderStateLicenseOutput[],
    ),
  ];

  const contactInfoItems = [
    {
      subtitle: localePendingReview?.officeDetails,
    },
    {
      label: localePendingReview?.officeOrganizationName,
      value: providerProfileData?.office_information?.officeName,
    },
    {
      label: localePendingReview?.emailAddress,
      value: providerProfileData?.office_information?.email,
    },
    {
      label: localePendingReview?.phone,
      value: `${providerProfileData?.office_information?.phone}`,
    },
    {
      label: localePendingReview?.address,
      value: [
        providerProfileData?.office_information?.address1,
        providerProfileData?.office_information?.address2,
        providerProfileData?.office_information?.city,
        providerProfileData?.office_information?.state,
        providerProfileData?.office_information?.zipCode,
        providerProfileData?.office_information?.country,
      ].filter(Boolean) as unknown[] as string[],
    },
    {
      subtitle: localePendingReview?.yourContactInfo,
    },
    {
      label: localePendingReview?.address,
      value: [
        providerProfileData?.address_information?.address1,
        providerProfileData?.address_information?.address2,
        providerProfileData?.address_information?.city,
        providerProfileData?.address_information?.state,
        providerProfileData?.address_information?.zipCode,
        providerProfileData?.address_information?.country,
      ].filter(Boolean) as unknown[] as string[],
    },
  ];

  const credentialsAndCertificationsItems = [
    {
      subtitle: localePendingReview?.credentials || 'Credentials',
    },
    {
      label: localePendingReview?.medicalRole,
      value: providerProfileData?.credentials?.medicalRole,
    },
    {
      label: localePendingReview?.yearsOfPractice,
      value: providerProfileData?.credentials?.yearsOfPractice,
    },
    {
      label: localePendingReview?.medicalSpecialization,
      value: providerProfileData?.credentials?.titles,
    },
    {
      label: localePendingReview?.boardCertifications,
      value: providerProfileData?.credentials?.certifications,
    },
    ...getEducationPart(providerProfileData?.education),
  ];

  const scheduleAndAvailabilityItems = getSchedulePart(schedule);

  const billingAndServicesItems = getBillingPart(
    providerProfileData?.serviceOfferings,
  );

  const navigate = useNavigate();

  const goToEdit = (section: ProviderProfileSectionTypes) => {
    navigate(MY_ACCOUNT_PROFILE, {
      state: {
        section,
        pendingApproveMode: true,
      },
    });
  };

  useEffect(() => {
    if (!code || !isFirstSendConnectStripe.current) return;

    isFirstSendConnectStripe.current = false;

    connectStripeAccountMutation({
      variables: {
        code: code,
      },
    })
      .then(() => {
        navigate(DASHBOARD, { replace: true });
      })
      .catch((error) => console.error(error));
  }, [code, connectStripeAccountMutation, navigate]);

  useEffect(() => {
    if (!providerProfileData?.acuityCalendarId) return;

    getAcuityProviderTimeSlots({
      variables: {
        providerCalendarId: providerProfileData.acuityCalendarId as string,
        date: formatCurrentDateToYYYYMMDD(new Date()),
      },
    });
  }, [getAcuityProviderTimeSlots, providerProfileData?.acuityCalendarId]);

  if (
    loadingPendingReview ||
    loadingMyProfile ||
    fhirProviderDataLoading ||
    !localePendingReview ||
    !localeMyProfile ||
    loadingUserVerificationStatus
  )
    return <Loader />;

  return (
    <>
      <div className="mx-4 col-span-12 flex flex-col justify-center items-center desktop:col-span-9 desktop:flex-row desktop:justify-start">
        <div className="flex flex-col mt-[15px] desktop:mt-0 desktop:ml-[15 px]">
          <h2 className="text-[28px] desktop:text-h1 text-dark-gray break-word">
            {localePendingReview?.title}
          </h2>
        </div>
      </div>
      {userVerificationStatus.status ===
        Persona_Inquiry_Status_Enum.Declined && (
        <div className="w-full mt-4">
          <Alert
            type="warning"
            customContent={
              <div className="flex desktop:flex-row desktop:justify-between desktop:items-center mobile:flex-col w-full">
                <p className="text-clc-red font-semibold desktop:w-10/12 mobile:mb-4 desktop:mb-0">
                  {localePendingReview?.notAbleToVerifyAccountRightNow}
                </p>
                <ButtonComponent
                  testID={'mailto'}
                  onClick={() =>
                    (window.location.href = `mailto:${SUPPORT_EMAIL}`)
                  }
                >
                  {localePendingReview?.contactUs}
                </ButtonComponent>
              </div>
            }
          />
        </div>
      )}

      <RequiredActions />

      <div className="flex flex-col items-center px-5 pt-5 pb-[30px] desktop:p-[40px] gap-5 bg-white rounded-10 mt-5">
        <div className="w-[690px] max-w-full">
          <div className="flex mt-[15px] flex-col gap-[50px] w-full">
            <p className="text-h6 text-btn-black font-semibold">
              {localePendingReview?.subtitle2}
            </p>

            <ProfileInfoSection
              title={localePendingReview?.accountInfo}
              fields={accountInfoItems}
              goToEdit={() =>
                goToEdit(ProviderProfileSectionTypes.ACCOUNT_INFO)
              }
            />

            <ProfileInfoSection
              title={localePendingReview?.personalInfo}
              fields={personalInfoItems}
              goToEdit={() =>
                goToEdit(ProviderProfileSectionTypes.PERSONAL_INFO)
              }
            />

            <ProfileInfoSection
              title={localePendingReview?.contactInfo}
              fields={contactInfoItems}
              goToEdit={() =>
                goToEdit(ProviderProfileSectionTypes.CONTACT_INFO)
              }
            />

            <ProfileInfoSection
              title={localePendingReview?.stateLicenses ?? 'NPI & License'}
              fields={stateLicensesItems}
              goToEdit={() => goToEdit(ProviderProfileSectionTypes.NPI)}
            />

            <ProfileInfoSection
              title={localePendingReview?.credentialsAndCertifications}
              fields={credentialsAndCertificationsItems}
              goToEdit={() =>
                goToEdit(
                  ProviderProfileSectionTypes.CREDENTIALS_AND_CERTIFICATIONS,
                )
              }
            />

            <div className="w-full">
              <ProfileInfoSection
                title={localePendingReview?.scheduleAndAvailability}
                fields={scheduleAndAvailabilityItems}
                goToEdit={() =>
                  goToEdit(
                    ProviderProfileSectionTypes.SCHEDULE_AND_AVAILABILITY,
                  )
                }
              />
              {!scheduleAndAvailabilityItems.length && (
                <EmptySchedulePlaceholder
                  goToScheduleForm={() =>
                    goToEdit(
                      ProviderProfileSectionTypes.SCHEDULE_AND_AVAILABILITY,
                    )
                  }
                />
              )}
            </div>

            <div className="w-full">
              <ProfileInfoSection
                title={localePendingReview?.billingAndServices}
                fields={billingAndServicesItems}
                goToEdit={() =>
                  goToEdit(ProviderProfileSectionTypes.BILLING_INFO)
                }
              />
              {!billingAndServicesItems.length && (
                <EmptyBillingPlaceholder
                  goToBillingForm={() =>
                    goToEdit(ProviderProfileSectionTypes.BILLING_INFO)
                  }
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

function EmptySchedulePlaceholder({
  goToScheduleForm,
}: {
  goToScheduleForm: () => void;
}) {
  const { data: localePendingReview } = useGetPage({
    locale: 'en',
    pageId: pageIds.PROVIDER_USER_PENDING_REVIEW,
  });

  return (
    <Alert
      desktopItemsAlign="items-start px-5 py-4"
      type="info"
      customContent={
        <div className="text-amber-border font-semibold flex flex-col desktop:flex-row gap-[15px]">
          {localePendingReview?.alertWithoutSchedule}
          <div>
            <ButtonComponent
              testID={'go-to-add-schedule'}
              onClick={goToScheduleForm}
              className={'whitespace-nowrap text-sm'}
            >
              {localePendingReview?.addSchedule}
            </ButtonComponent>
          </div>
        </div>
      }
    />
  );
}

function EmptyBillingPlaceholder({
  goToBillingForm,
}: {
  goToBillingForm: () => void;
}) {
  const { data: localePendingReview } = useGetPage({
    locale: 'en',
    pageId: pageIds.PROVIDER_USER_PENDING_REVIEW,
  });

  return (
    <Alert
      desktopItemsAlign="items-start px-5 py-4"
      type="info"
      customContent={
        <div className="text-amber-border font-semibold flex flex-col desktop:flex-row gap-[15px]">
          {localePendingReview?.alertWithoutBilling}
          <div>
            <ButtonComponent
              testID={'go-to-add-billing'}
              onClick={goToBillingForm}
              className={'whitespace-nowrap text-sm'}
            >
              {localePendingReview?.addBilling}
            </ButtonComponent>
          </div>
        </div>
      }
    />
  );
}
